import { motion } from "framer-motion";
import {
  LineChart,
  Compass,
  Newspaper,
  Zap,
  Briefcase,
  Headphones,
  CalendarDays,
  Radio,
} from "lucide-react";

const BENEFITS = [
  { icon: LineChart, title: "Daily Market Analysis", desc: "A clear breakdown of the day's setup, shared before the session begins." },
  { icon: Compass, title: "Market Bias", desc: "A daily directional view — bullish, bearish or sideways — with the reasoning behind it." },
  { icon: Newspaper, title: "Daily Major Event News", desc: "The events that actually move gold prices, filtered and explained in plain terms." },
  { icon: Zap, title: "Daily VIP Trades", desc: "Planned setups with entry, target and stop-loss — always shared with full reasoning." },
  { icon: Briefcase, title: "6+ Business Exposure", desc: "Beyond charts — practical exposure to more than six income businesses." },
  { icon: Headphones, title: "24/7 Support", desc: "Member support around the clock, every day of the week." },
  { icon: CalendarDays, title: "Sat–Sun Live Talks", desc: "Weekend live sessions on strategy, trading psychology and open Q&A." },
  { icon: Radio, title: "Daily Live Trade Sessions", desc: "Watch trades being planned and managed live, in real time." },
];

const Benefits = () => (
  <section id="benefits" className="relative overflow-hidden py-28 sm:py-36" data-testid="benefits-section">
    <div className="pointer-events-none absolute -left-32 top-16 h-96 w-96 rounded-full bg-[radial-gradient(circle,rgba(168,85,247,0.2),transparent_70%)] blur-3xl" />
    <div className="pointer-events-none absolute -right-32 bottom-16 h-[28rem] w-[28rem] rounded-full bg-[radial-gradient(circle,rgba(124,58,237,0.16),transparent_70%)] blur-3xl" />
    <div className="pointer-events-none absolute left-1/2 top-1/2 h-80 w-[60rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(ellipse,rgba(192,132,252,0.12),transparent_70%)] blur-3xl" />
    <div className="relative mx-auto max-w-6xl px-5 sm:px-8">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8 }}
        className="mb-16 max-w-2xl"
      >
        <p className="font-mono2 text-[11px] uppercase tracking-[0.3em] text-slate-500">(02) — The Edge</p>
        <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-[#0a0a0a] sm:text-5xl">
          What you get <span className="font-accent text-grad-aurora">inside the room</span>
        </h2>
        <p className="mt-4 text-base text-slate-600 sm:text-lg">
          Eight core services included with every membership, delivered on a daily schedule.
        </p>
      </motion.div>

      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {BENEFITS.map((b, i) => (
          <motion.div
            key={b.title}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, delay: (i % 4) * 0.08 }}
            data-testid={`benefit-card-${i}`}
            className="glass card-aurora lift group rounded-2xl p-6"
          >
            <div className="mb-5 inline-flex rounded-xl bg-[#0a0a0a] p-3 transition-transform duration-300 group-hover:scale-105">
              <b.icon className="h-5 w-5 text-white" />
            </div>
            <h3 className="font-sub text-lg font-semibold text-[#0a0a0a]">{b.title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">{b.desc}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

export default Benefits;
